import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { Intent } from "@blueprintjs/core";
import useMaskedInputState from "../../hooks/useMaskedInputState";
import DefaultToaster from "../../atoms/DefaultToaster";
import { getProduct } from "./queries";
import { editProduct } from "./mutations";
import useTutorialsState from "../../hooks/useTutorialsState";
import useTagAccount from "../../hooks/useTagAccount";

const defaultTemplate = `# Things are moving right along!

Your order for the {{product.name}} is in 
and {{product.owner.name}} will contact 
you soon with your fulfillment information.

<a class="button" href="{{receiptLink}}">
  View Your Receipt
</a>`;

export const useChangeConfirmationEmail = ({ productId }) => {
  const { data, loading } = useQuery(getProduct, {
    variables: { id: productId },
    skip: !productId
  });

  const [confirmationEmail, setConfirmationEmail] = useMaskedInputState("", {
    maxLength: 1000
  });

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState();
  const { getHasCompletedTutorial, completeTutorial } = useTutorialsState();
  const { tagAccount } = useTagAccount();

  const product = data && data.products && data.products[0];

  useEffect(() => {
    product && setConfirmationEmail(product.orderConfirmationEmail);
  }, [product]);

  const [updateConfirmationEmail] = useMutation(editProduct, {
    refetchQueries: [{ query: getProduct, variables: { id: productId } }]
  });

  const resetEmail = async () => {
    if (
      product &&
      product.orderConfirmationEmail &&
      product.orderConfirmationEmail == defaultTemplate
    ) {
      setConfirmationEmail(defaultTemplate);
    } else {
      await saveChanges(defaultTemplate);
    }
  };

  const saveChanges = async (defaultTemplate) => {
    setIsSaving(true);
    setError(undefined);

    try {
      await updateConfirmationEmail({
        variables: {
          product: {
            id: productId,
            orderConfirmationEmail: defaultTemplate || confirmationEmail
          },
          ...(product.variants
            ? {
                variants: {
                  edited: product.variants.map((variant) => ({
                    id: variant.id,
                    orderConfirmationEmail: defaultTemplate || confirmationEmail
                  }))
                }
              }
            : {})
        }
      });

      const hasCustomizedOrderConfirmationEmail = getHasCompletedTutorial(
        "Add Customized Order Confirmation Email"
      );

      if (!hasCustomizedOrderConfirmationEmail) {
        await completeTutorial({
          variables: {
            name: "Add Customized Order Confirmation Email",
            version: 1
          }
        });

        await tagAccount("Order Confirmation Customized");
      }

      setIsSaving(false);

      DefaultToaster.show({
        message: "Order confirmation email updated!",
        icon: "tick",
        intent: Intent.SUCCESS
      });
    } catch (err) {
      setIsSaving(false);
      setError({
        message: "An error occurred while updating the record."
      });
    }
  };

  return {
    confirmationEmail,
    setConfirmationEmail,
    isSaving,
    error,
    saveChanges,
    resetEmail,
    resetEnabled: defaultTemplate !== confirmationEmail,
    loading: loading || !data,
    isSaveEnabled:
      confirmationEmail &&
      product &&
      confirmationEmail !== product.orderConfirmationEmail &&
      !isSaving,
    productName: product && product.name
  };
};
